import * as React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  ThumbDown as ThumbDownIcon,
  ThumbDownOutlined as ThumbDownOutlinedIcon,
  Refresh as RefreshIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Message, type ChunkDocuments } from "./types";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import { MarkdownRenderer } from "../common/MarkdownRenderer";

interface ChatAiMessageProps {
  message: Message;
  onOpenDocuments?: (documents: ChunkDocuments) => void;
  feedbackValue?: 1 | -1;
  onLike?: () => void;
  onDislike?: () => void;
  onResetLike?: () => void;
  onRetry?: () => void;
  retryDisabled?: boolean;
}

/** Sostituisce [CHUNK-XXXX] e [CHUNK-251, CHUNK-250] con link markdown cliccabili */
function processChunkRefs(content: string): string {
  return content.replace(/\[([^\]]*CHUNK-\d+[^\]]*)\]/gi, (full, inner) => {
    return inner.replace(
      /CHUNK-(\d+)/gi,
      (_m: string, num: string) => `[\\[${num}\\]](chunk:${num})`
    );
  });
}

export const ChatAiMessage: React.FC<ChatAiMessageProps> = ({
  message,
  onOpenDocuments,
  feedbackValue,
  onLike,
  onDislike,
  onResetLike,
  onRetry,
  retryDisabled = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const hasLike = feedbackValue === 1;
  const hasDislike = feedbackValue === -1;
  const responseMeta = message.responseMeta;
  const wordCount = React.useMemo(() => {
    return message.content.split(/\s+/).filter((word) => word.length > 0)
      .length;
  }, [message.content]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        mb: 0,
        gap: 0,
      }}
    >
      {/* Avatar */}
      <Avatar
        src="/assets/round_light.png"
        alt="GreenEye"
        sx={{
          width: 39,
          height: 39,
          bgcolor: "transparent",
          marginTop: 0,
        }}
      />

      {/* Message Container */}
      <Paper
        sx={{
          flex: 1,
          maxWidth: "100%",
          p: 1,
          bgcolor: "#FFFFFF",
          boxShadow: "none",
        }}
      >
        {/* Sender Name */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: "bold",
            color: (theme) => theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          GreenEye
        </Typography>

        {/* Model-unavailability warning banner (429 / 503) */}
        {message.isModelUnavailable && !message.isStreaming && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,152,0,0.12)"
                  : "rgba(255,152,0,0.08)",
              border: "1px solid",
              borderColor: "warning.light",
              borderRadius: 2,
              px: 1.5,
              py: 1,
              mb: 1.5,
            }}
          >
            <WarningAmberRoundedIcon
              fontSize="small"
              sx={{ color: "warning.main", mt: "2px", flexShrink: 0 }}
            />
            <Typography variant="body2" sx={{ color: "warning.dark" }}>
              {message.content}
            </Typography>
          </Box>
        )}

        {/* Message Content - Mostrato solo quando c'è contenuto o non sta pensando */}
        {!message.isModelUnavailable &&
          (!message.isStreaming || message.content.trim().length > 0) && (
          <Box
            sx={{
              color: (theme) => theme.palette.text.primary,
              mb: 1.5,
              "& p": {
                margin: 0,
                marginBottom: "0.5rem",
                "&:last-child": {
                  marginBottom: 0,
                },
              },
              "& ul, & ol": {
                margin: 0,
                paddingLeft: "1.5rem",
                marginBottom: "0.5rem",
              },
              "& code": {
                bgcolor: "grey.100",
                padding: "0.125rem 0.25rem",
                borderRadius: "0.25rem",
                fontSize: "0.875em",
                fontFamily: "monospace",
              },
              "& pre": {
                bgcolor: "grey.100",
                padding: "0.75rem",
                borderRadius: "0.25rem",
                overflow: "auto",
                marginBottom: "0.5rem",
                "& code": {
                  bgcolor: "transparent",
                  padding: 0,
                },
              },
            }}
          >
            <MarkdownRenderer
              components={{
                a: ({ href, children, ...props }) => {
                  if (href == "") {
                    const num = Number(
                      (children as string).replace(/[\[\]]/g, "")
                    );
                    const key = `CHUNK-${num}`;
                    const filtered =
                      message.documents && message.documents[key]
                        ? { [key]: message.documents[key] }
                        : undefined;
                    if (!filtered) {
                      // Il documento sorgente è stato rigenerato/rimosso dopo questa conversazione:
                      // la citazione resta visibile nel testo storico ma diventa inattiva.
                      return (
                        <Tooltip
                          title="Contenuto non più disponibile: il documento è stato aggiornato"
                          enterDelay={300}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: "text.disabled",
                              fontWeight: "bold",
                              cursor: "default",
                            }}
                          >
                            {children}
                          </Box>
                        </Tooltip>
                      );
                    }
                    return (
                      <Box
                        component="span"
                        sx={{
                          color: "primary.main",
                          fontWeight: "bold",
                          cursor: onOpenDocuments ? "pointer" : "default",
                          "&:hover": onOpenDocuments
                            ? { textDecoration: "underline" }
                            : {},
                        }}
                        onClick={() => {
                          if (filtered && onOpenDocuments)
                            onOpenDocuments(filtered);
                        }}
                        role={onOpenDocuments ? "button" : undefined}
                      >
                        {children}
                      </Box>
                    );
                  }
                  return (
                    <a href={href} {...props}>
                      {children}
                    </a>
                  );
                },
              }}
            >
              {processChunkRefs(message.content)}
            </MarkdownRenderer>
          </Box>
        )}

        {/* Interaction Icons e Metadata - Solo quando la risposta è completata */}
        {!message.isStreaming && (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              justifyContent: "space-between",
              alignItems: "end",
              mb: 6,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Tooltip title="Copia messaggio" enterDelay={300}>
                <IconButton
                  size="small"
                  sx={{ color: (theme) => theme.palette.grey[600] }}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(message.content);
                      toast.success("Messaggio copiato");
                    } catch (err) {
                      console.error("Failed to copy text to clipboard:", err);
                      toast.error("Errore durante la copia");
                    }
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {onLike !== undefined && (
                <Tooltip
                  title={hasLike ? "Rimuovi like" : "Utile"}
                  enterDelay={300}
                >
                  <IconButton
                    size="small"
                    onClick={() => (hasLike ? onResetLike?.() : onLike())}
                    sx={{
                      color: hasLike
                        ? (theme) => theme.palette.primary.main
                        : (theme) => theme.palette.grey[600],
                    }}
                  >
                    {hasLike ? (
                      <ThumbUpIcon fontSize="small" />
                    ) : (
                      <ThumbUpOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {onDislike !== undefined && (
                <Tooltip
                  title={hasDislike ? "Rimuovi dislike" : "Non utile"}
                  enterDelay={300}
                >
                  <IconButton
                    size="small"
                    onClick={() => (hasDislike ? onResetLike?.() : onDislike())}
                    sx={{
                      color: hasDislike
                        ? (theme) => theme.palette.error.main
                        : (theme) => theme.palette.grey[600],
                    }}
                  >
                    {hasDislike ? (
                      <ThumbDownIcon fontSize="small" />
                    ) : (
                      <ThumbDownOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Fonti citate" enterDelay={300}>
                <span>
                  <IconButton
                    size="small"
                    sx={{ color: (theme) => theme.palette.grey[600] }}
                    onClick={() => {
                      if (message.documents && onOpenDocuments) {
                        onOpenDocuments(message.documents);
                      }
                    }}
                    disabled={
                      !message.documents ||
                      Object.keys(message.documents).length === 0
                    }
                  >
                    <ImportContactsIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {onRetry && (
                <Tooltip title="Riprova" enterDelay={300}>
                  <span>
                    <IconButton
                      size="small"
                      sx={{ color: (theme) => theme.palette.grey[600] }}
                      onClick={onRetry}
                      disabled={retryDisabled}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.75rem",
                color: (theme) => theme.palette.grey[500],
                mt: 1,
                mb: 0.5,
              }}
            >
              {!isMobile && message.responseMeta && (message.responseMeta.model != null || message.responseMeta.use_pro_model !== undefined) && (
                <Typography component="span" variant="caption">
                  Modello: {message.responseMeta.use_pro_model ? "performante" : "veloce"}
                </Typography>
              )}
              {!isMobile && message.responseMeta?.response_type && (
                <>
                  <span>•</span>
                  <Typography component="span" variant="caption">
                    Risposta: {message.responseMeta.response_type}
                  </Typography>
                </>
              )}
              {!isMobile && (
                <>
                  <span>•</span>
                  <Typography component="span" variant="caption">
                    Parole: {wordCount}
                  </Typography>
                </>
              )}
              {!isMobile && (message.responseTime !== undefined || (message.responseMeta?.elapsed_ms != null)) && (
                <>
                  <span>•</span>
                  <Typography component="span" variant="caption">
                    Tempo:{" "}
                    {message.responseTime != null
                      ? `${message.responseTime.toFixed(2)}s`
                      : `${((message.responseMeta?.elapsed_ms ?? 0) / 1000).toFixed(2)}s`}
                  </Typography>
                </>
              )}
              {message.timestamp && (
                <>
                  {!isMobile &&
                    (responseMeta != null || message.responseTime !== undefined) && (
                    <span>•</span>
                  )}
                  <Typography component="span" variant="caption">
                    {new Date(message.timestamp).toLocaleDateString("it-IT", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
